// SeatDashboard — instructor cohort control center for a classroom.
// Shows per-student rollup + bulk invite tools.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Users, Mail, Copy, Check, Trash2, Loader2, Send, UserPlus, Flame,
  BarChart3, Trophy, TrendingUp, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import {
  getSeatDashboard, bulkInvite, listInvites, revokeInvite,
} from "@/lib/api";

// ---------- Bulk invite modal ----------
const BulkInviteModal = ({ open, classroom, onClose, onDone }) => {
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!open) { setEmails(""); setResult(null); setBusy(false); }
  }, [open]);

  const submit = async () => {
    if (!emails.trim() || busy) return;
    setBusy(true);
    try {
      const r = await bulkInvite(classroom.id, emails);
      setResult(r);
      toast.success(`Invited ${r.invited} · Skipped ${r.skipped}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't invite");
    } finally { setBusy(false); }
  };

  const copyLink = async (url, id) => {
    const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    await navigator.clipboard.writeText(full);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogTitle className="sr-only">Bulk invite students</DialogTitle>
        <DialogDescription className="sr-only">Paste emails to send class invitations.</DialogDescription>
        <div className="p-6 bg-gradient-to-br from-indigo-100/60 to-fuchsia-100/60 dark:from-indigo-950/40 dark:to-fuchsia-950/40">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/60 dark:bg-black/30 border border-border px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="h-3 w-3 text-accent" /> Cohort
          </div>
          <h2 className="mt-3 font-serif text-2xl font-black">Invite students to {classroom.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Paste emails — one per line, or comma-separated. We'll mint a unique invite link for each and skip anyone already invited or enrolled.</p>
        </div>
        <div className="p-6 space-y-4">
          {!result ? (
            <>
              <textarea
                data-testid="bulk-invite-emails"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                rows={7}
                placeholder="alice@school.edu&#10;bob@school.edu, charlie@school.edu&#10;…"
                className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition font-mono"
              />
              <button
                data-testid="bulk-invite-submit"
                disabled={!emails.trim() || busy}
                onClick={submit}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-3 font-semibold disabled:opacity-40 hover:opacity-90 transition"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" /> Send invites</>}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                Or share your class code <span className="font-mono font-bold">{classroom.code}</span> — students can enter it on <span className="font-mono">/join</span>.
              </p>
            </>
          ) : (
            <div>
              <p className="text-sm">
                <span className="font-semibold text-accent">{result.invited}</span> invited ·
                <span className="ml-1"><span className="font-semibold">{result.skipped}</span> skipped (already invited or enrolled)</span>
              </p>
              <ul data-testid="invite-links" className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                {(result.invites || []).map((inv) => (
                  <li key={inv.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono truncate">{inv.email}</p>
                      <p className="text-muted-foreground truncate">{inv.join_url}</p>
                    </div>
                    <button
                      onClick={() => copyLink(inv.join_url, inv.id)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 hover:border-accent/50 transition"
                    >
                      {copied === inv.id ? <><Check className="h-3 w-3 text-emerald-500" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setResult(null)}
                className="mt-4 w-full rounded-full border border-border py-2 text-sm font-semibold hover:border-accent/50 transition"
              >
                Invite more
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Stat card ----------
const StatCard = ({ icon: Icon, label, value, tone = "default" }) => (
  <div className={`rounded-2xl border p-4 ${tone === "accent" ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="mt-1.5 text-3xl font-black font-mono">{value ?? "—"}</p>
  </div>
);

// ---------- Seat dashboard ----------
export const SeatDashboard = ({ classroom }) => {
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [tab, setTab] = useState("seats"); // seats | invites

  const load = async () => {
    const [d, i] = await Promise.all([
      getSeatDashboard(classroom.id).catch(() => null),
      listInvites(classroom.id).catch(() => []),
    ]);
    setData(d);
    setInvites(i || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classroom.id]);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this pending invite?")) return;
    try { await revokeInvite(classroom.id, id); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't revoke"); }
  };

  if (!data) return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>;

  const t = data.totals;

  return (
    <div data-testid="seat-dashboard">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h3 className="flex items-center gap-2 font-serif text-xl font-bold">
          <BarChart3 className="h-5 w-5" /> Seat Dashboard
        </h3>
        <button
          data-testid="open-bulk-invite"
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition"
        >
          <UserPlus className="h-4 w-4" /> Invite students
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Users} label="Seats" value={t.seats} tone="accent" />
        <StatCard icon={TrendingUp} label="Active · 7d" value={t.active_last_7d} />
        <StatCard icon={Trophy} label="Class avg" value={t.class_avg_score !== null ? `${t.class_avg_score}%` : "—"} />
        <StatCard icon={Mail} label="Pending invites" value={data.invites.pending} />
      </div>

      <div className="flex rounded-full border border-border p-0.5 w-fit mb-3">
        <button
          data-testid="seat-tab-seats"
          onClick={() => setTab("seats")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${tab === "seats" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >Seats · {data.seats.length}</button>
        <button
          data-testid="seat-tab-invites"
          onClick={() => setTab("invites")}
          className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${tab === "invites" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >Invites · {invites.length}</button>
      </div>

      {tab === "seats" && (
        data.seats.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-10 text-center">
            <Users className="h-8 w-8 mx-auto text-accent" />
            <p className="mt-3 font-serif text-lg">No students yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Send invites — they'll appear here as they join.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-secondary/30 border-b border-border">
              <div className="col-span-5 sm:col-span-4">Student</div>
              <div className="col-span-3 sm:col-span-2 text-center">Streak</div>
              <div className="col-span-4 sm:col-span-3 text-center">Assignments</div>
              <div className="hidden sm:block sm:col-span-2 text-center">Avg</div>
              <div className="hidden sm:block sm:col-span-1 text-right">Made</div>
            </div>
            <ul className="divide-y divide-border">
              {data.seats.map((s) => (
                <li key={s.student_id} data-testid={`seat-${s.student_id}`} className="grid grid-cols-12 gap-2 items-center px-4 py-3">
                  <div className="col-span-5 sm:col-span-4 flex items-center gap-2 min-w-0">
                    <Avatar id={s.avatar} size={28} streak={s.current_streak} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{s.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{s.email}</p>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-center">
                    <span className="inline-flex items-center gap-0.5 text-orange-500 font-mono font-bold">
                      <Flame className="h-3.5 w-3.5" /> {s.current_streak}
                    </span>
                  </div>
                  <div className="col-span-4 sm:col-span-3 text-center font-mono text-sm">
                    <span className="font-bold text-foreground">{s.assignments_completed}</span>
                    <span className="text-muted-foreground"> / {s.assignments_total}</span>
                  </div>
                  <div className="hidden sm:block sm:col-span-2 text-center font-mono text-sm">
                    {s.avg_score !== null ? (
                      <span className={`font-bold ${s.avg_score >= 80 ? "text-emerald-500" : s.avg_score >= 60 ? "text-amber-500" : "text-rose-500"}`}>{s.avg_score}%</span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="hidden sm:block sm:col-span-1 text-right font-mono text-xs text-muted-foreground">{s.lectures_created}</div>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      {tab === "invites" && (
        invites.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-10 text-center">
            <Mail className="h-8 w-8 mx-auto text-accent" />
            <p className="mt-3 font-serif text-lg">No invites yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Click "Invite students" to send a batch.</p>
          </div>
        ) : (
          <ul className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
            {invites.map((inv) => (
              <li key={inv.id} data-testid={`invite-${inv.id}`} className="px-4 py-3 flex items-center gap-3">
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                  inv.status === "joined" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" :
                  inv.status === "pending" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" :
                  "bg-muted text-muted-foreground"
                }`}>{inv.status}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{inv.email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{inv.status === "joined" ? `Joined ${inv.joined_at?.slice(0,10) || ""}` : `Invited ${inv.created_at?.slice(0,10) || ""}`}</p>
                </div>
                {inv.status === "pending" && (
                  <>
                    <button
                      onClick={async () => {
                        const full = inv.join_url.startsWith("http") ? inv.join_url : `${window.location.origin}${inv.join_url}`;
                        await navigator.clipboard.writeText(full);
                        toast.success("Invite link copied");
                      }}
                      title="Copy link"
                      className="p-2 rounded-full hover:bg-secondary/60 transition"
                    ><Copy className="h-4 w-4" /></button>
                    <button
                      data-testid={`revoke-${inv.id}`}
                      onClick={() => revoke(inv.id)}
                      title="Revoke"
                      className="p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                    ><X className="h-4 w-4" /></button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      <BulkInviteModal open={inviteOpen} classroom={classroom} onClose={() => setInviteOpen(false)} onDone={load} />
    </div>
  );
};

export default SeatDashboard;
